import {
  PartnerSettingsPartnerReadWebformFontEnum,
  PartnerSettingsTranslationPartnerReadTypeEnum,
} from '@/api';
import useEmplacementsStore from '@/composables/store/useEmplacementsStore';
import { computed } from 'vue';
import { useStore } from '@/store';

export default function usePartnerStore() {
  const store = useStore();
  const primaryColor = computed(
    () => store.state.partner.partner?.partnerSettings?.color ?? '',
  );
  const secondaryColor = computed(
    () => store.state.partner.partner?.partnerSettings?.secondaryColor ?? '',
  );
  const fontName = computed(
    () =>
      store.state.partner.partner?.partnerSettings?.webformFont ??
      PartnerSettingsPartnerReadWebformFontEnum.Replica,
  );
  const useRoundedBoxes = computed(
    () =>
      store.state.partner.partner?.partnerSettings?.webformRoundedBoxes ?? true,
  );
  const logoFile = computed(
    () => store.state.partner.partner.logoFilename ?? '',
  );
  const faviconFilename = computed(
    () => store.state.partner.partner.faviconFilename ?? '',
  );
  const partnerName = computed(() => store.state.partner.partner.name ?? '');
  const partnerEmail = computed(
    () => store.state.partner.partner.contact?.email ?? '',
  );
  const partnerPhone = computed(
    () => store.state.partner.partner.contact?.phonenumber ?? '',
  );
  const termsUrl = computed(
    () => store.state.partner.partner?.partnerSettings?.termsUrl ?? '',
  );
  const lateCheckoutEnabled = computed(
    () =>
      store.state.partner.partner?.partnerSettings?.lateCheckoutEnabled ??
      false,
  );
  const headerImages = computed(
    () => store.state.partner.partner?.partnerSettings?.webformImages ?? [],
  );

  const fetchPartner = () => store.dispatch('partner/fetchPartner');

  const texts = computed(
    () => store.state.partner.partner?.partnerSettings?.translations ?? [],
  );
  const getIntroText = (locale: string): string | undefined => {
    return texts.value.find(
      (translation) =>
        translation.language === locale.substring(0, 2) &&
        translation.type ===
          PartnerSettingsTranslationPartnerReadTypeEnum.WebformIntro,
    )?.name;
  };

  const { emplacements } = useEmplacementsStore();

  const defaultEmplacement = computed(() => {
    const id =
      store.state.partner.partner?.partnerSettings?.defaultEmplacement?.replace(
        '/api/emplacements/',
        '',
      );
    const match = emplacements.value?.find(
      (emplacement) => emplacement.id === parseInt(id ?? ''),
    );
    if (match) {
      return match;
    }
    return emplacements.value?.find(
      (emplacement) => emplacement.name?.toLocaleLowerCase() === 'u-shape',
    );
  });
  const bigGroupsDefaultEmplacement = computed(() => {
    const id =
      store.state.partner.partner?.partnerSettings?.bigGroupsDefaultEmplacement?.replace(
        '/api/emplacements/',
        '',
      );
    return emplacements.value?.find(
      (emplacement) => emplacement.id === parseInt(id ?? ''),
    );
  });

  const bigGroupsThreshold = computed(
    () => store.state.partner.partner?.paxMiceThreshold ?? 999,
  );

  // Google Tag Manager ID
  const gtmId = computed(
    () =>
      store.state.partner.partner?.partnerSettings?.analyticsKey ?? undefined,
  );

  const partnerWebsite = computed(
    () => store.state.partner.partner.partnerSettings?.website ?? undefined,
  );

  const partnerCurrency = computed(
    () => store.state.partner.partner.partnerSettings?.currency ?? undefined,
  );

  return {
    fetchPartner,
    primaryColor,
    secondaryColor,
    logoFile,
    faviconFilename,
    partnerName,
    partnerEmail,
    partnerPhone,
    termsUrl,
    lateCheckoutEnabled,
    headerImages,
    fontName,
    useRoundedBoxes,
    getIntroText,
    defaultEmplacement,
    bigGroupsDefaultEmplacement,
    bigGroupsThreshold,
    gtmId,
    partnerWebsite,
    partnerCurrency,
  };
}
